import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function ResultCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editFilePreview, setEditFilePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      file: null,
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      file: isEdit ? Yup.mixed().nullable() : Yup.mixed().required("File is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.file) {
        formData.append("file", values.file);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("results/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("results/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Result updated successfully!" : "Result created successfully!");
          navigate("/admin/results");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchResult = async () => {
        setLoading(true);
        try {
          const response = await request.get(`results/${id}`);
          const item = response.data.data;
          formik.setValues({
            title: item.title || "",
            description: item.description || "",
            file: null,
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setEditFilePreview(imagepathReturn(item.file || null));
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Result" : "Create New Result"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/results"),
              }}
              primaryButton={{
                label: isEdit ? "Update Result" : "Create Result",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="title" label="Title" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="description"
                    label="Description"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <FileUpload
                    name="file"
                    formik={formik}
                    label="Upload File (PDF, JPG, PNG)"
                    editImagePreview={editFilePreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}